import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        // Trigger confetti celebration
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      } else if (data.status === 'expired' || data.payment_status === 'failed') {
        setStatus('failed');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2"><BrandTitle>Invalid Session</BrandTitle></h1>
            <p className="text-slate-400 mb-6">No payment session found.</p>
            <Link to="/pricing">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                View Pricing
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-slate-800 border-slate-700">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2"><BrandTitle>Processing Payment</BrandTitle></h1>
              <p className="text-slate-400">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && paymentData && (
            <>
              <div className="relative">
                <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
                <Sparkles className="w-6 h-6 text-yellow-400 absolute top-0 right-1/4 animate-pulse" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2"><BrandTitle>Payment Successful!</BrandTitle></h1>
              <p className="text-slate-400 mb-6">
                Welcome to CARTIEAI {paymentData.package}!
              </p>
              
              <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Plan</span>
                  <span className="text-white font-medium">{paymentData.package}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Amount</span>
                  <span className="text-emerald-400 font-medium">
                    ${paymentData.amount}/{paymentData.currency === 'usd' ? 'mo' : paymentData.currency}
                  </span>
                </div>
                {paymentData.user_email && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Email</span>
                    <span className="text-white">{paymentData.user_email}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Link to="/dashboard" className="block">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-xs text-slate-400">
                  A confirmation email has been sent to your inbox.
                </p>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2"><BrandTitle>Payment Failed</BrandTitle></h1>
              <p className="text-slate-400 mb-6">
                We couldn't process your payment. Please try again.
              </p>
              <Link to="/pricing">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  Try Again
                </Button>
              </Link>
            </>
          )}

          {status === 'timeout' && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2"><BrandTitle>Payment Pending</BrandTitle></h1>
              <p className="text-slate-400 mb-6">
                Your payment is still being processed. Please check your email for confirmation.
              </p>
              <Link to="/dashboard">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
